import React, { useCallback, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { TralenexLogo } from "@/components/brand/TralenexLogo";
import { NAV_SECTIONS, SETTINGS_SECTION } from "./navConfig";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useAuth } from "@/contexts/AuthContext";
import { can } from "@/lib/rbac";
import { isInFlight } from "@/lib/deploymentWorkflow";
import { cn } from "@/lib/utils";
import { NavSection } from "./NavSection";

const DEFAULT_OPEN_GROUPS = { govern: true, measure: true, deploy: true };

/**
 * Filter the nav tree for the active persona using `lib/rbac.can()`.
 * A parent section appears if (a) it has a `to` and the persona has
 * `view:<surface>`, or (b) at least one child is visible.
 */
function filterSectionForPersona(section, personaId) {
  if (section.to) {
    return can(personaId, `view:${section.surface}`) ? section : null;
  }
  const children = (section.children || []).filter((c) => can(personaId, `view:${c.surface}`));
  if (!children.length) return null;
  return { ...section, children };
}

/**
 * Sidebar (left navigation)
 *  - Expanded (260 px): TRALENEX combination logo + ALGOS wordmark + caption + tier badge
 *  - Collapsed (64 px): TRALENEX logo at small height + icon rail
 *  - Nav sections are filtered by the active persona (RBAC).
 */
export default function Sidebar({ collapsed, onToggle }) {
  const { tenant, requests } = useTenantData();
  const { user } = useAuth();
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState(DEFAULT_OPEN_GROUPS);

  const toggleGroup = useCallback((id) => {
    setOpenGroups((s) => ({ ...s, [id]: !s[id] }));
  }, []);

  // Compute the in-flight deployment count for the Deploy nav badge.
  // Uses the canonical `isInFlight()` helper so the badge ALWAYS matches
  // the "In-flight requests" KPI on /deploy/requests (stage < 5, non-terminal,
  // non-rejected).
  const inFlightDeployCount = useMemo(
    () => (requests || []).filter(isInFlight).length,
    [requests],
  );

  const visibleSections = useMemo(
    () =>
      NAV_SECTIONS.map((section) => {
        const filtered = filterSectionForPersona(section, user?.id);
        if (!filtered) return null;
        if (filtered.id === "deploy") {
          return { ...filtered, badge: inFlightDeployCount || undefined };
        }
        return filtered;
      }).filter(Boolean),
    [user?.id, inFlightDeployCount],
  );
  const settingsSection = useMemo(
    () => filterSectionForPersona(SETTINGS_SECTION, user?.id),
    [user?.id],
  );

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 bottom-0 z-40 bg-raised border-r border-border-subtle flex flex-col transition-[width] duration-200",
        collapsed ? "w-[64px]" : "w-[260px]",
      )}
      data-testid="app-sidebar"
      aria-label="Primary navigation"
    >
      <SidebarBrand collapsed={collapsed} tenant={tenant} />

      <nav className="flex-1 overflow-y-auto py-3" data-testid="sidebar-nav">
        {visibleSections.map((section) => (
          <NavSection
            key={section.id}
            section={section}
            collapsed={collapsed}
            open={openGroups[section.id]}
            onToggle={() => toggleGroup(section.id)}
            currentPath={location.pathname}
          />
        ))}

        {settingsSection && (
          <>
            <div className="my-3 mx-3 hairline" />
            <NavSection
              section={settingsSection}
              collapsed={collapsed}
              open={openGroups[settingsSection.id]}
              onToggle={() => toggleGroup(settingsSection.id)}
              currentPath={location.pathname}
            />
          </>
        )}
      </nav>

      <SidebarFooter collapsed={collapsed} onToggle={onToggle} />
    </aside>
  );
}

function SidebarBrand({ collapsed, tenant }) {
  return (
    <div
      className={cn(
        "px-4 pt-5 pb-4 border-b border-border-subtle",
        collapsed && "px-2",
      )}
    >
      <Link
        to="/dashboard"
        className="flex items-center gap-2.5"
        data-testid="sidebar-tralenex-link"
        aria-label="TRALENEX home"
      >
        <TralenexLogo height={44} className={collapsed ? "mx-auto" : undefined} />
      </Link>
      {!collapsed && (
        <div className="mt-4">
          <span className="text-h3 text-ink-primary tracking-tight">ALGOS</span>
        </div>
      )}
    </div>
  );
}

function SidebarFooter({ collapsed, onToggle }) {
  return (
    <div
      className={cn(
        "px-4 py-3 border-t border-border-subtle text-[11px] text-ink-secondary",
        collapsed && "px-2 text-center",
      )}
    >
      {!collapsed ? (
        <div className="flex items-center justify-between">
          <span>ALGOS · A product of TRALENEX · v1.0</span>
          <button
            type="button"
            onClick={onToggle}
            className="text-ink-secondary hover:text-ink-primary"
            aria-label="Collapse sidebar"
            data-testid="sidebar-collapse-toggle"
          >
            <PanelLeftClose size={16} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={onToggle}
          className="text-ink-secondary hover:text-ink-primary mx-auto block"
          aria-label="Expand sidebar"
          data-testid="sidebar-expand-toggle"
        >
          <PanelLeftOpen size={16} />
        </button>
      )}
    </div>
  );
}
